import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { formatDate } from 'utils/formatDate';
import normalizeRegistrationDate from 'helpers/normalizeRegistrationDate';
import { useNotificationsRealtime } from 'context/notifications/NotificationsRealtimeContext';
import {
  getDocumentRequests,
  getSupervisors,
} from 'services/adminNegotiationService';
import { persistStateToUrl } from 'utils/urlState';
import { adminNegotiationI18n } from './i18n';
import AdminNegotiationChecklistDetail from './AdminNegotiationChecklistDetail';
import AdminNegotiationProcessModal from './AdminNegotiationProcessModal';
import DatePickerEs from '../../components/DatePickerEs/DatePickerEs';
import '../../styles/naEeppTableGrid.scss';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) =>
  e && typeof e === 'object' && 'target' in e ? e.target.value : e;

// Estado ÚNICO de la fila, derivado client-side (C2). Orden de prioridad:
// viability_pending (GC) → denied → canceled (proceso terminado) → aprobada → en curso →
// programada (confirmed sin recolección pedida) → unknown (estado no reconocido/legacy,
// badge neutro "—"). canceled va antes de aprobada/en curso: un proceso terminado puede
// tener collectionRequestedAt de cuando estaba en recolección y no debe mislabelearse.
// El pool de documentos sin clasificar NO es un estado: lo comunica el chip +N de la
// columna Recibidos (unclassifiedCount).
const recolStatus = (row) => {
  const vs = (row.viabilityStatus || '').toLowerCase();
  if (vs === 'viability_pending') return 'viability_pending';
  if (vs === 'denied') return 'denied';
  if (vs === 'canceled') return 'canceled';
  if (row.documentsApprovedAt) return 'approved';
  if (row.collectionRequestedAt) return 'ongoing';
  // Confirmada y aún sin recolección pedida (collectionRequestedAt ya descartado arriba).
  if (vs === 'confirmed') return 'scheduled';
  // Cualquier otro estado no reconocido: no lo mislabeleamos como programada.
  return 'unknown';
};

const recolStatusColor = (status) => {
  switch (status) {
    case 'viability_pending':
      return 'warning';
    case 'denied':
      return 'error';
    case 'canceled':
      // Gris: mismo token neutro que `unknown` (proceso terminado, NO error).
      return 'light';
    case 'approved':
      return 'success';
    case 'ongoing':
      return 'primary';
    case 'scheduled':
      return 'info';
    case 'unknown':
    default:
      return 'light';
  }
};

/**
 * Página propia de Documentos (Negociación Administrativa). Dos bloques:
 *   - Tabla de Requests = checks aprobados + counts del checklist (botón ojo → detalle).
 *     La columna Recibido muestra un badge contador de documentos recibidos sin
 *     clasificar (pool) cuando los hay.
 *   - Detalle del request (modal) = checklist (espejo de ChecklistSection) + Redacción
 *     cuando el checklist está completo + pool de documentos sin clasificar
 *     (componente AdminNegotiationChecklistDetail).
 *
 * Solo design system vía prop DesignSystemComponents.
 */
export default function AdminNegotiationDocuments({ DesignSystemComponents }) {
  const { t } = useScopedTranslation(adminNegotiationI18n);
  const { subscribeToAdminNegotiation } = useNotificationsRealtime();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    Table,
    Badge,
    Card,
    Button,
    IconButton,
    Modal,
    Skeleton,
    Dropdown,
    Input,
  } = DesignSystemComponents ?? {};

  // ── Requests ────────────────────────────────────────────────────────────
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [selectedCheck, setSelectedCheck] = useState(null);
  // Estado de la tabla inicializado desde la URL, para que al volver del detalle se restaure.
  const [page, setPage] = useState(() => Number(searchParams.get('page') ?? 0));
  const [pageSize, setPageSize] = useState(() =>
    Number(searchParams.get('pageSize') ?? 10)
  );
  const [sortKey, setSortKey] = useState(
    () => searchParams.get('sortKey') ?? null
  );
  const [sortDirection, setSortDirection] = useState(
    () => searchParams.get('sortDir') ?? null
  );
  // Filtros adicionales de la tabla de Solicitudes. searchInput = valor controlado del
  // buscador; searchQuery = valor debounced (400ms) que efectivamente dispara el fetch.
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get('search') ?? ''
  );
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('search') ?? ''
  );
  const [recolStatusFilter, setRecolStatusFilter] = useState(
    () => searchParams.get('recolStatus') ?? ''
  );
  // Filtro por abogado asignado (value = userId como string; '' = todos).
  const [assignedUserFilter, setAssignedUserFilter] = useState(
    () => searchParams.get('assignedUserId') ?? ''
  );
  const [users, setUsers] = useState([]);
  // Rango de fecha de recolección como objeto de Dates (mismo patrón que Clients):
  // guardamos Dates en el estado y normalizamos a strings yyyy-MM-dd solo al construir
  // la query. El objeto de estado se pasa tal cual como initialRange (identidad estable
  // entre renders: solo cambia al setearlo, así un refetch realtime no pisa una selección
  // en curso).
  const [collectionRange, setCollectionRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [totalCount, setTotalCount] = useState(0);

  // Modal de proceso (Nuevo proceso / decidir un viability_pending desde la fila).
  // null = cerrado; { mode, checkId?, clientLabel?, initialDecision? }.
  const [processModal, setProcessModal] = useState(null);

  // Error visible en el banner de la tabla de Requests.
  const [error, setError] = useState(null);
  // Aviso informativo del deep-link (?checkId= que no está en la página cargada).
  const [deepLinkNotice, setDeepLinkNotice] = useState(null);

  const requestsAbortRef = useRef(null);
  const debounceRef = useRef(null);
  // ?checkId= pendiente de consumir; el effect del deep-link lo limpia al resolverlo.
  const deepLinkCheckIdRef = useRef(searchParams.get('checkId'));
  // Ref a las filas cargadas: fetchRequests la lee para decidir el refetch "silencioso"
  // (ya hay data → no togglear loading) sin sumar `requests` a sus deps.
  const requestsRef = useRef([]);
  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  const fetchRequests = useCallback(
    async ({ silent } = {}) => {
      if (requestsAbortRef.current) requestsAbortRef.current.abort();
      const controller = new AbortController();
      requestsAbortRef.current = controller;
      // Refetch silencioso (realtime / post-acción / post-modal): con filas ya cargadas NO
      // togglear loading (evita el flash de skeleton de toda la tabla y de la página de
      // fondo) ni limpiar error/deepLinkNotice.
      const quiet = silent && requestsRef.current.length > 0;
      if (!quiet) {
        setRequestsLoading(true);
        setError(null);
        setDeepLinkNotice(null);
      }
      // Normalizamos el rango de Dates a strings yyyy-MM-dd solo acá (formato que ya
      // espera el back); el wire sigue siendo collectionFrom/collectionTo.
      const normalizedRange = normalizeRegistrationDate(collectionRange);
      try {
        const data = await getDocumentRequests(
          {
            page,
            pageSize,
            search: searchQuery || undefined,
            recolStatus: recolStatusFilter || undefined,
            collectionFrom: normalizedRange?.dateFrom || undefined,
            collectionTo: normalizedRange?.dateTo || undefined,
            assignedUserId: assignedUserFilter || undefined,
            sortBy: sortKey || undefined,
            desc: sortDirection ? sortDirection === 'desc' : undefined,
          },
          controller.signal
        );
        setRequests(Array.isArray(data?.items) ? data.items : []);
        setTotalCount(data?.totalCount ?? 0);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        if (!quiet) setRequestsLoading(false);
      }
    },
    [
      page,
      pageSize,
      searchQuery,
      recolStatusFilter,
      collectionRange,
      assignedUserFilter,
      sortKey,
      sortDirection,
      t,
    ]
  );

  useEffect(() => {
    fetchRequests();
    return () => requestsAbortRef.current?.abort();
  }, [fetchRequests]);

  // persistStateToUrl reemplaza TODOS los params, así que hay que arrastrar el ?checkId= o lo
  // borraríamos antes de que su effect lo lea. Va por ref para no meter la URL en las deps.
  useEffect(() => {
    persistStateToUrl({
      filters: {
        search: searchQuery,
        recolStatus: recolStatusFilter,
        assignedUserId: assignedUserFilter,
        checkId: deepLinkCheckIdRef.current ?? '',
      },
      sortKey,
      sortDirection,
      page,
      pageSize,
      setSearchParams,
    });
  }, [
    searchQuery,
    recolStatusFilter,
    assignedUserFilter,
    sortKey,
    sortDirection,
    page,
    pageSize,
    setSearchParams,
  ]);

  const handleServerSortChange = ({ sortKey: key, direction } = {}) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setPage(0);
  };

  // Debounce del buscador (400ms): el texto tipeado (searchInput) baja a searchQuery, que
  // es lo que dispara el fetch. Cada cambio vuelve a la primera página (setPage(0)).
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Supervisores vigentes para el filtro "Abogado asignado" (antes: todos los usuarios).
  // El value es userId, que es contra lo que filtra el back.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const list = await getSupervisors(controller.signal);
        const options = (Array.isArray(list) ? list : [])
          .filter((s) => s.active && s.userActive && s.userId > 0)
          .map((s) => ({ label: s.name || s.email, value: s.userId }))
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));
        setUsers(options);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        setUsers([]);
      }
    })();
    return () => controller.abort();
  }, []);

  // Realtime: nos suscribimos al grupo global de NA; cuando el backend emite
  // admin_negotiation_updated (documento clasificado, docs listos, documentación
  // aprobada) refetchamos la tabla de Requests. Debounce 400ms para coalescer ráfagas
  // (mismo patrón que EscriturasList).
  useEffect(() => {
    const unsubscribe = subscribeToAdminNegotiation(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchRequests({ silent: true });
      }, 400);
    });

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      unsubscribe();
    };
  }, [subscribeToAdminNegotiation, fetchRequests]);

  // Clamp de la página: si un evento realtime o un borrado baja el totalCount por
  // debajo de la página actual, la reajustamos a la última válida para no dejar la
  // tabla vacía. El effect de fetch (que depende de page) re-pide la página correcta.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [totalCount, pageSize, page]);

  // Deep-link ?checkId= (del botón "Revisar tarea" de las tabs / del 409 del guard):
  // tras cargar la lista, busca la fila y abre el modal correcto según su estado —
  // decide si viability_pending, checklist en cualquier otro caso (confirmed). Si la
  // fila no está en la página cargada, avisa. En todos los casos limpia el query param
  // (fetch directo cuando no está visible queda diferido — el 409 implica proceso reciente).
  useEffect(() => {
    if (requestsLoading) return;
    const raw = searchParams.get('checkId');
    if (!raw) return;
    const targetId = Number(raw);
    const row = Number.isNaN(targetId)
      ? null
      : requests.find((r) => r.viabilityCheckId === targetId);
    if (row) {
      if ((row.viabilityStatus || '').toLowerCase() === 'viability_pending') {
        setProcessModal({
          mode: 'decide',
          checkId: row.viabilityCheckId,
          clientLabel: row.contactIdentifier,
          initialDecision: 'approve',
        });
      } else {
        setSelectedCheck(row.viabilityCheckId);
      }
    } else {
      setDeepLinkNotice(t('docs_deeplink_not_visible'));
    }
    deepLinkCheckIdRef.current = null;
    const next = new URLSearchParams(searchParams);
    next.delete('checkId');
    setSearchParams(next, { replace: true });
  }, [requestsLoading, requests, searchParams, setSearchParams, t]);

  // Badge del estado único de la fila (C2), derivado de campos ya existentes.
  const recolStatusBadge = useCallback(
    (row) => {
      const status = recolStatus(row);
      const label = t(`doc_recol_${status}`);
      return Badge ? (
        <Badge text={label} color={recolStatusColor(status)} size='sm' />
      ) : (
        <span>{label}</span>
      );
    },
    [Badge, t]
  );

  // Opciones del filtro por Estado (Todos = value ''); reusa las labels doc_recol_*
  // existentes con el mismo set/valores que recolStatus().
  const recolFilterOptions = useMemo(
    () => [
      { label: t('req_filter_all'), value: '' },
      { label: t('doc_recol_viability_pending'), value: 'viability_pending' },
      { label: t('doc_recol_denied'), value: 'denied' },
      { label: t('doc_recol_canceled'), value: 'canceled' },
      { label: t('doc_recol_scheduled'), value: 'scheduled' },
      { label: t('doc_recol_ongoing'), value: 'ongoing' },
      { label: t('doc_recol_approved'), value: 'approved' },
    ],
    [t]
  );
  const selectedRecolOption = useMemo(
    () =>
      recolFilterOptions.find((o) => o.value === recolStatusFilter) ??
      recolFilterOptions[0],
    [recolFilterOptions, recolStatusFilter]
  );

  // Opciones del filtro por abogado asignado: "Todos" + los supervisores del módulo.
  const assignedUserOptions = useMemo(
    () => [
      { label: t('req_filter_all'), value: '' },
      ...users.map((u) => ({ label: u.label, value: String(u.value) })),
    ],
    [users, t]
  );
  const selectedAssignedUserOption = useMemo(
    () =>
      assignedUserOptions.find((o) => o.value === assignedUserFilter) ??
      assignedUserOptions[0],
    [assignedUserOptions, assignedUserFilter]
  );

  // Columnas de la tabla de Requests.
  const requestColumns = useMemo(
    () => [
      {
        accessor: 'clientIdentifier',
        sort: true,
        label: t('doc_col_rut'),
        // RUT del cliente (C1, aditivo); fallback al teléfono si aún no llega. clientName
        // visible como línea secundaria (patrón del checklist).
        render: (row) => {
          const rut = row.clientIdentifier || row.contactIdentifier || '—';
          return (
            <div>
              <span>{rut}</span>
              {row.clientName && (
                <p
                  className='mb-0'
                  style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}
                >
                  {row.clientName}
                </p>
              )}
            </div>
          );
        },
      },
      {
        accessor: 'recolStatus',
        sort: true,
        label: t('doc_col_recol_status'),
        render: (row) => recolStatusBadge(row),
      },
      {
        accessor: 'createdAt',
        sort: true,
        label: t('doc_col_started_at'),
        // Inicio del proceso = Create_At del check (back: CreatedAt). Distinto de la
        // columna de recolección, que es de la etapa siguiente. "—" si no hay fecha.
        render: (row) => (row.createdAt ? formatDate(row.createdAt) : '—'),
      },
      {
        accessor: 'collectionDate',
        sort: true,
        label: t('doc_col_collection_date'),
        // Si ya se solicitó (collectionRequestedAt) mostramos esa fecha; si no, la
        // fecha estimada de recolección (collectionScheduledAt). "—" si ninguna.
        render: (row) => {
          const requested = row.collectionRequestedAt;
          const scheduled = row.collectionScheduledAt;
          if (requested) return formatDate(requested);
          if (scheduled) return formatDate(scheduled);
          return '—';
        },
      },
      {
        accessor: 'recibidos',
        sort: true,
        label: t('req_col_received'),
        // Conteo dispuesto/aplicable: numerador = recibidos + aprobados; denominador =
        // total − no_aplica (el rechazado queda en el denominador, muestra el hueco). Más el
        // badge contador de documentos recibidos sin clasificar (pool del check), solo si hay
        // alguno. El conteo del pool llega del back en unclassifiedCount.
        render: (row) => (
          <span className='d-flex align-items-center gap-1'>
            <span>{`${(row.summary?.recibidos ?? 0) + (row.summary?.aprobados ?? 0)}/${(row.summary?.total ?? 0) - (row.summary?.noAplica ?? 0)}`}</span>
            {row.unclassifiedCount > 0 && Badge && (
              <span title={t('req_unclassified_hint')}>
                <Badge
                  text={String(row.unclassifiedCount)}
                  color='warning'
                  size='sm'
                />
              </span>
            )}
          </span>
        ),
      },
      {
        accessor: 'assignedUsers',
        sort: true,
        label: t('req_assigned'),
        // Abogado(s) de la tarea ancla VIAB/RENEG (backend: AssignedUsers[{id,name}]).
        render: (row) => {
          const names = (row.assignedUsers || [])
            .map((u) => u.name)
            .filter(Boolean);
          if (names.length === 0) return '—';
          const text = names.join(', ');
          return (
            <span title={text} style={{ fontSize: 13 }}>
              {text}
            </span>
          );
        },
      },
      {
        accessor: 'actions',
        label: t('doc_col_actions'),
        render: (row) => {
          if (!IconButton) return null;
          // viability_pending (inyectado por GC): abre el modal en modo decisión.
          if (
            (row.viabilityStatus || '').toLowerCase() === 'viability_pending'
          ) {
            return (
              <IconButton
                ariaLabel={t('req_open')}
                icon='eye'
                size='md'
                stroke='var(--primary-500)'
                onClick={() =>
                  setProcessModal({
                    mode: 'decide',
                    checkId: row.viabilityCheckId,
                    clientLabel: row.contactIdentifier,
                    initialDecision: 'approve',
                  })
                }
              />
            );
          }
          return (
            <IconButton
              ariaLabel={t('req_open')}
              icon='eye'
              size='md'
              stroke='var(--primary-500)'
              onClick={() => setSelectedCheck(row.viabilityCheckId)}
            />
          );
        },
      },
    ],
    [t, recolStatusBadge, Badge, IconButton]
  );

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('doc_title')}</h2>
          <h4 className='fw-normal'>{t('doc_page_subtitle')}</h4>
        </div>
        <div className='d-flex gap-2 align-items-center'>
          {Button && (
            <Button
              label={t('proc_new')}
              size='sm'
              variant='primary'
              onClick={() => setProcessModal({ mode: 'create' })}
            />
          )}
          {IconButton && (
            <IconButton
              ariaLabel={t('doc_refresh')}
              icon='arrowCounterclockwise'
              size='lg'
              iconFill='var(--primary-500)'
              onClick={() => fetchRequests()}
              disabled={requestsLoading}
            />
          )}
        </div>
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {deepLinkNotice && (
          <div className='alert alert-info py-2' style={{ fontSize: 13 }}>
            {deepLinkNotice}
          </div>
        )}

        {/* Tabla de Requests */}
        <h5 className='fw-semibold mb-2' style={{ fontSize: 14 }}>
          {t('req_section_title')}
        </h5>

        {/* Filtros de la tabla de Solicitudes: búsqueda + estado + rango de fecha de
          recolección. Cada cambio (incl. el buscador debounced) vuelve a page 0. */}
        <div className='row g-2 align-items-end mb-3'>
          <div className='col-md-3'>
            <label className='form-label caption mb-1'>
              {t('doc_filter_search')}
            </label>
            {Input ? (
              <Input
                placeholder={t('doc_filter_search_placeholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(readVal(e))}
                width='100%'
              />
            ) : (
              <input
                className='form-control form-control-sm'
                placeholder={t('doc_filter_search_placeholder')}
                aria-label={t('doc_filter_search')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            )}
          </div>
          {Dropdown && (
            <div className='col-md-3 mb-4'>
              <label className='form-label caption mb-1'>
                {t('doc_filter_recol')}
              </label>
              <Dropdown
                options={recolFilterOptions}
                value={selectedRecolOption}
                onChange={(opt) => {
                  setRecolStatusFilter(opt?.value ?? '');
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
          {Dropdown && (
            <div className='col-md-3 mb-4'>
              <label className='form-label caption mb-1'>
                {t('req_assigned')}
              </label>
              <Dropdown
                options={assignedUserOptions}
                value={selectedAssignedUserOption}
                onChange={(opt) => {
                  setAssignedUserFilter(opt?.value ?? '');
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
          {/* Rango de fecha de recolección: DatePickerEs (mismo componente local que Clients).
            Guardamos el rango de Dates crudo en el estado y se lo pasamos tal cual como
            initialRange (identidad estable). El picker solo dispara onChange con un rango
            completo de 2 fechas, así que no puede limpiarse solo: el botón "Limpiar"
            resetea el estado (que a su vez limpia el input vía el effect de initialRange). */}
          <div className='col-md-4 mb-4'>
            <DatePickerEs
              labelTextLeft={t('doc_filter_collection_from')}
              labelTextRight={t('doc_filter_collection_to')}
              initialRange={collectionRange}
              onChange={(range) => {
                setCollectionRange({
                  startDate: range?.startDate ?? null,
                  endDate: range?.endDate ?? null,
                });
                setPage(0);
              }}
            />
            {collectionRange.startDate && Button && (
              <Button
                label={t('doc_filter_clear_dates')}
                size='sm'
                variant='naked'
                onClick={() => {
                  setCollectionRange({ startDate: null, endDate: null });
                  setPage(0);
                }}
              />
            )}
          </div>
        </div>

        {requestsLoading && requests.length === 0 ? (
          <div className='d-flex flex-column gap-2 mb-4'>
            {Skeleton ? (
              [0, 1, 2].map((i) => (
                <Skeleton key={i} height='2.5rem' width='100%' />
              ))
            ) : (
              <p className='font-light'>{t('doc_loading')}</p>
            )}
          </div>
        ) : (
          <div className='table-container mb-4 na-eepp-grid-7'>
            {Table ? (
              <Table
                columns={requestColumns}
                data={requests}
                loading={requestsLoading}
                paginationMode='server'
                paginator
                paginatorGroup={[
                  { label: '10', value: 10 },
                  { label: '20', value: 20 },
                  { label: '50', value: 50 },
                  { label: '100', value: 100 },
                ]}
                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(s) => {
                  setPageSize(s);
                  setPage(0);
                }}
                totalItems={totalCount}
                serverSortKey={sortKey}
                serverSortDirection={sortDirection}
                onServerSortChange={handleServerSortChange}
              />
            ) : null}
            {!requestsLoading && totalCount === 0 && (
              <div className='text-center p-4'>
                <p className='caption font-light'>{t('req_empty')}</p>
              </div>
            )}
          </div>
        )}

        {/* Detalle del request (checklist + redacción) */}
        {selectedCheck != null && Modal && (
          <Modal
            title={t('req_detail_title')}
            show
            onClose={() => setSelectedCheck(null)}
            width='1100px'
            primaryBtnTxt={null}
            secondaryBtnTxt={null}
          >
            <AdminNegotiationChecklistDetail
              DesignSystemComponents={DesignSystemComponents}
              viabilityCheckId={selectedCheck}
              t={t}
              onApproved={() => fetchRequests({ silent: true })}
              onChanged={() => fetchRequests({ silent: true })}
            />
          </Modal>
        )}

        {/* Nuevo proceso / decidir un viability_pending */}
        {processModal && (
          <AdminNegotiationProcessModal
            DesignSystemComponents={DesignSystemComponents}
            mode={processModal.mode}
            checkId={processModal.checkId}
            clientLabel={processModal.clientLabel}
            initialDecision={processModal.initialDecision}
            onClose={() => setProcessModal(null)}
            onCompleted={() => {
              setProcessModal(null);
              fetchRequests({ silent: true });
            }}
          />
        )}
      </ContentWrapper>
    </div>
  );
}
